import React, { Children, cloneElement, isValidElement, useEffect, useRef, useState } from 'react'
import anime from 'animejs'
import classNames from 'classnames'
import { detectDevice } from '@/utils/detectDevice'
import { DEFAULT_LANDING_ANIMATION_DURATION, LANDING_ANIMATION, LANDING_ANIMATION_MARGIN } from './constants'
import type { TLandingAnimationProps } from './types'
import './styles.scss'

export const LandingAnimation: React.FC<TLandingAnimationProps> = ({
  isDisabled,
  desktopTargetStyles,
  children,
  mobileTargetStyles = LANDING_ANIMATION.fadeUp,
  onComplete,
}) => {
  const [isStarted, setIsStarted] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  const animationOptionsRef = useRef({
    desktopTargetStyles,
    mobileTargetStyles,
    onComplete,
  })

  useEffect(() => {
    if (isDisabled) {
      return () => {}
    }

    const { isDesktop } = detectDevice()
    const targetStyles = isDesktop ? animationOptionsRef.current.desktopTargetStyles : animationOptionsRef.current.mobileTargetStyles
    const rootMargin = isDesktop ? LANDING_ANIMATION_MARGIN.desktop : LANDING_ANIMATION_MARGIN.mobile

    const observer = new IntersectionObserver(
      ([entity]) => {
        const { isIntersecting } = entity || {}

        if (isIntersecting) {
          anime({
            ...DEFAULT_LANDING_ANIMATION_DURATION,
            targets: ref.current,
            changeBegin: () => setIsStarted(true),
            changeComplete: animationOptionsRef.current.onComplete,
            ...targetStyles,
          })

          observer.disconnect()
        }
      },
      {
        rootMargin,
      },
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [isDisabled])

  const onlyChild = Children.only(children)

  if (isDisabled) {
    return <>{onlyChild}</>
  }

  return (
    <>
      {Children.map(onlyChild, child => {
        if (!isValidElement(child)) {
          return null
        }

        return cloneElement(child, {
          // @ts-ignore pass ref to the element even if it has no ref prop
          ref,
          className: classNames(child.props.className, !isStarted && 'landingAnimation--hidden'),
        })
      })}
    </>
  )
}
